import { getProto } from './pb-imports';
import { ImClient } from './imClient';
import { chatRepository } from '../db/repository';
import { eventBus } from '../eventBus';
import type { DBUser, DBGroup, DBSession } from '../db/database';
import type { SessionKind, ChatMessage, ChatSession } from '@domain/chat/types';
import { AUDIO_PREVIEW_TEXT } from '@shared/utils/audio';
import { playMessageNotificationSound } from '@shared/utils/messageNotificationSound';
import { buildFileMessagePayload, buildOnlineFileMessagePayload, encodeFileMessagePayload, fileMessagePreview, parseFileMessagePayload } from '@domain/file/messagePayload';
import { callMessagePreview, isCallMessageType } from '@domain/call/message';
import { shouldPlayForegroundMessageSound } from './messageSoundPolicy.js';
import { extractImagePayload } from '@shared/utils/ui';
import {
  resolveDirectMessageSessionId,
  resolveIncomingUnreadCount,
  resolveSyncedUnreadCount,
  shouldResetSessionHistory,
} from './sessionConsistency.js';
import { collectGroupIdsForSync } from './recentSessionSync.js';
import { fetchAllFriendIds } from '@domain/contacts/api';

export function initials(value: string | undefined | null, fallback = 'LC') {
  const text = (value ?? '').trim();
  if (!text) {
    return fallback;
  }
  const parts = text.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return text.slice(0, 2).toUpperCase();
}

export function sessionKeyOf(sessionType: SessionKind, sessionId: number) {
  const typeNum = sessionType === 'group' ? 2 : 1;
  return `${typeNum}_${sessionId}`;
}

const SESSION_TYPE_SINGLE = 1;
const SESSION_TYPE_GROUP = 2;

export function sessionTypeToProtoValue(sessionType: SessionKind) {
  return sessionType === 'group' ? SESSION_TYPE_GROUP : SESSION_TYPE_SINGLE;
}

export function sessionKindFromProtoValue(value: number): SessionKind {
  return value === SESSION_TYPE_GROUP ? 'group' : 'single';
}

function msgTextToPreview(text: string): string {
  if (extractImagePayload(text)) return '[图片]';
  return text;
}

function nextLocalMessageId() {
  return `msg_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

function ensureSuccessfulAck(msgId: number): number {
  if (msgId <= 0) {
    throw new Error('Message was blocked or rejected by server');
  }
  return msgId;
}

export class ImManager {
  private static instance: ImManager;

  public userMap = new Map<number, DBUser>();
  public groupMap = new Map<number, DBGroup>();
  public sessionMap = new Map<string, DBSession>();

  public imClient: ImClient | null = null;
  public userId: number | null = null;
  private isBootstrapping = false;
  private currentSessionKey: string | null = null;

  private constructor() {}

  public static getInstance(): ImManager {
    if (!ImManager.instance) {
      ImManager.instance = new ImManager();
    }
    return ImManager.instance;
  }

  // Bind the client from login
  public attachClient(client: ImClient, userId: number) {
    this.imClient = client;
    this.userId = userId;
  }

  public setCurrentSessionKey(sessionKey: string | null) {
    this.currentSessionKey = sessionKey;
  }

  public async initData(apiBase?: string) {
    if (!this.imClient || !this.userId) return;
    if (this.isBootstrapping) return;
    this.isBootstrapping = true;

    try {
      // Relationship APIs own the user set; IM no longer transports complete
      // profile snapshots. Store ID-only rows here and let IdentityCoordinator
      // hydrate canonical names and media over bounded HTTP batches.
      const [friendIds, normalGroupsRsp] = await Promise.all([
        fetchAllFriendIds(apiBase),
        this.imClient.requestNormalGroups(this.userId).catch(() => null),
      ]);

      const updatedUsers: DBUser[] = [];
      this.userMap.clear();
      for (const friendId of friendIds) {
        if (friendId === this.userId) {
          continue;
        }
        const u = {
          id: friendId,
          name: `用户 #${friendId}`,
          email: '',
          phone: '',
          avatar: '',
          signInfo: '',
        };
        updatedUsers.push(u);
        this.userMap.set(u.id, u);
      }
      await chatRepository.saveUsers(updatedUsers);

      const updatedGroups = new Map<number, DBGroup>();
      const baseGroupIds = normalGroupsRsp
        ? normalGroupsRsp.getGroupVersionListList().map((item: any) => item.getGroupId())
        : [];

      // 2. Load sessions
      const recentSessionsRsp = await this.imClient.requestRecentContactSessions(this.userId, 0);
      const recentInfos = recentSessionsRsp.getContactSessionListList();
      const unreadRsp = await this.imClient.requestUnreadMessageCounts(this.userId).catch(() => null);
      const recentGroupIds = recentInfos
        .filter((info: any) => sessionKindFromProtoValue(info.getSessionType()) === 'group')
        .map((info: any) => info.getSessionId());
      const groupIds = collectGroupIdsForSync({
        baseGroupIds,
        recentGroupIds,
      });

      if (groupIds.length > 0) {
        try {
          const groupInfoRsp = await this.imClient.requestGroupInfoByIds(this.userId, groupIds);
          for (const groupInfo of groupInfoRsp.getGroupInfoListList()) {
            const g = {
              id: groupInfo.getGroupId(),
              name: groupInfo.getGroupName() || `群组 #${groupInfo.getGroupId()}`,
              avatar: groupInfo.getGroupAvatar() || '',
              shieldStatus: groupInfo.getShieldStatus?.() || 0,
              memberIds: groupInfo.getGroupMemberListList?.() || [],
            };
            updatedGroups.set(g.id, g);
            this.groupMap.set(g.id, g);
          }
          await chatRepository.saveGroups([...updatedGroups.values()]);
        } catch (e) {
          console.warn('Sync groups failed:', e);
        }
      }
      
      const unreadCountBySession = new Map<string, number>();
      if (unreadRsp) {
        for (const unreadInfo of unreadRsp.getUnreadinfoListList()) {
          const key = sessionKeyOf(sessionKindFromProtoValue(unreadInfo.getSessionType()), unreadInfo.getSessionId());
          unreadCountBySession.set(key, unreadInfo.getUnreadCnt());
        }
      }

      for (const info of recentInfos) {
        const sessionType = sessionKindFromProtoValue(info.getSessionType());
        const sessionId = info.getSessionId();
        if (sessionType === 'single' && sessionId === this.userId) {
          console.warn('[self-filter] skip self single session from remote sync', { sessionId });
          continue;
        }
        const sessionKey = sessionKeyOf(sessionType, sessionId);
        const remoteLatestMsgId = info.getLatestMsgId();
        const localLatestMsgId = await chatRepository.getSessionLatestMsgId(sessionKey);

        if (shouldResetSessionHistory(localLatestMsgId, remoteLatestMsgId)) {
          console.warn(
            '[SESSION_RESET_DEBUG] msgId regression detected, clearing stale local history',
            { sessionKey, localLatestMsgId, remoteLatestMsgId },
          );
          await chatRepository.clearSessionMessages(sessionKey);
        }
        
        let lastMsgText = '';
        const msgBytes = info.getLatestMsgData_asU8?.();
        if (msgBytes && msgBytes.length > 0) {
          const msgType = info.getLatestMsgType?.() || 1; // Default to text if missing
          lastMsgText = this.decodeMsgData(msgBytes, msgType, true);
        }

        const sessionObj: ChatSession = {
          sessionKey,
          sessionId,
          sessionType,
          updatedAt: info.getUpdatedTime() * 1000,
          lastMsgId: remoteLatestMsgId,
          lastMsgPreview: lastMsgText,
          lastMsgFromUserId: 0,
          unreadCount: resolveSyncedUnreadCount({
            sessionKey,
            currentSessionKey: this.currentSessionKey,
            remoteUnreadCount: unreadCountBySession.get(sessionKey) || 0,
          }),
          title: '',
          subtitle: '',
          avatarText: '',
          messages: [],
          historyState: 'idle',
          historyError: null,
          hasMoreHistory: true,
        };

        await chatRepository.saveSession(sessionObj);
        this.sessionMap.set(sessionKey, sessionObj as any);
      }

      const sessionsList = await chatRepository.getAllSessions();
      this.resolveSessions(sessionsList);
      
      const legacyContactsMap: any = {};
      for(const [, u] of this.userMap) { legacyContactsMap[u.id] = { ...u, avatarUrl: u.avatar }; }
      const legacyGroupsMap: any = {};
      for(const [, g] of this.groupMap) {
        legacyGroupsMap[g.id] = { ...g, avatarUrl: g.avatar, memberIds: g.memberIds || [] };
      }

      eventBus.emit('ContactsUpdateEvent', { contacts: legacyContactsMap, groups: legacyGroupsMap });
      await this.notifySessionsUpdate();
    } catch (error) {
      console.error('IMManager initData err:', error);
    } finally {
      this.isBootstrapping = false;
    }
  }

  public async handleIncomingMessage(payload: any) {
    if (!this.userId) return;

    const sessionId = payload.sessionType === 'single'
      ? resolveDirectMessageSessionId({
          ownerUserId: this.userId,
          fromUserId: payload.fromUserId,
          toSessionId: payload.toSessionId,
        })
      : payload.sessionId;
    if (sessionId === null) return;
    const sessionKey = sessionKeyOf(payload.sessionType, sessionId);
    const timestamp = (payload.createTime || Math.floor(Date.now() / 1000)) * 1000;
    const isEcho = payload.fromUserId === this.userId;
    const text = payload.text || this.decodeMsgData(payload.msgData ?? new Uint8Array(), payload.msgType);
    const fileMeta = parseFileMessagePayload(payload.msgData, payload.msgType);

    const message: ChatMessage = {
      localId: nextLocalMessageId(),
      msgId: payload.msgId,
      sessionKey,
      sessionId,
      sessionType: payload.sessionType,
      from: payload.fromUserId,
      text,
      msgType: payload.msgType,
      msgData: payload.msgData,
      fileMeta,
      ts: timestamp,
      dir: isEcho ? 'out' : 'in',
      status: 'sent',
    };

    let shouldNotify = true;

    if (isEcho && payload.msgId > 0) {
      const optimisticMessage = await chatRepository.findOptimisticMessageForEcho(
        sessionKey, payload.fromUserId, text, timestamp, 30000
      );
      if (optimisticMessage) {
        await chatRepository.updateMessageWithServerId(optimisticMessage.localId, payload.msgId);
        message.localId = optimisticMessage.localId;
      } else {
        shouldNotify = await chatRepository.saveMessage(message);
      }
    } else {
      shouldNotify = await chatRepository.saveMessage(message);
    }

    if (!shouldNotify) {
      return;
    }

    // Update Session
    let session = await chatRepository.getSession(sessionKey);
    if (!session) {
      session = {
        sessionKey,
        sessionId,
        sessionType: payload.sessionType,
        updatedAt: timestamp,
        lastMsgId: payload.msgId,
        lastMsgPreview: msgTextToPreview(text),
        lastMsgFromUserId: payload.fromUserId,
        unreadCount: 0,
        title: '',
        subtitle: '',
        avatarText: '',
        messages: [],
        historyState: 'idle',
        historyError: null,
        hasMoreHistory: true,
      };
    }

    if (session) {
      session.updatedAt = timestamp;
      session.lastMsgId = payload.msgId;
      session.lastMsgPreview = msgTextToPreview(text);
      session.lastMsgFromUserId = payload.fromUserId;
      session.unreadCount = resolveIncomingUnreadCount({
        sessionKey,
        currentSessionKey: this.currentSessionKey,
        previousUnreadCount: session.unreadCount,
        isEcho,
      });
      await chatRepository.saveSession(session);
    }

    try {
      this.imClient?.ackMsgRead(
        this.userId,
        sessionId,
        sessionTypeToProtoValue(payload.sessionType),
        payload.msgId
      );
    } catch { }

    // Notify sessions first (mirrors Flutter's updateSession → SessionUpdateEvent order),
    // then fire NewMsgEvent last so that the chat window's async DB query wins and is
    // never overwritten by the subsequent SessionUpdateEvent state merge.
    await this.notifySessionsUpdate();
    if (shouldPlayForegroundMessageSound({
      isEcho,
      sessionKey,
      currentSessionKey: this.currentSessionKey,
      isCallMessage: isCallMessageType(payload.msgType),
    })) {
      playMessageNotificationSound();
    }
    eventBus.emit('NewMsgEvent', message);
  }

  public async sendTextMsg(text: string, sessionId: number, sessionType: SessionKind): Promise<ChatMessage> {
    if (!this.imClient || !this.userId) throw new Error("Client not initialized");
    
    const sessionKey = sessionKeyOf(sessionType, sessionId);
    const msg: ChatMessage = {
      localId: nextLocalMessageId(),
      msgId: 0,
      sessionKey,
      sessionId,
      sessionType,
      from: this.userId,
      text,
      ts: Date.now(),
      dir: 'out',
      status: 'pending',
    };

    await chatRepository.saveMessage(msg);
    
    // Update Session Optimistically
    let session = await chatRepository.getSession(sessionKey);
    if (!session) {
      session = {
        sessionKey,
        sessionId,
        sessionType,
        updatedAt: msg.ts,
        lastMsgId: 0,
        lastMsgPreview: text,
        lastMsgFromUserId: this.userId,
        unreadCount: 0,
        title: '',
        subtitle: '',
        avatarText: '',
        messages: [],
        historyState: 'idle',
        historyError: null,
        hasMoreHistory: true,
      };
    } else {
      session.updatedAt = msg.ts;
      session.lastMsgPreview = text;
      session.lastMsgFromUserId = this.userId;
    }
    
    if (session) {
      await chatRepository.saveSession(session);
    }

    eventBus.emit('NewMsgEvent', msg); // Optimistic UI update (pending)

    try {
      const ack = await this.imClient.sendTextMessage(
        this.userId,
        sessionId,
        sessionTypeToProtoValue(sessionType),
        text
      );
      const serverMsgId = ensureSuccessfulAck(ack.getMsgId?.() ?? 0);
      if (serverMsgId > 0) {
        await chatRepository.updateMessageWithServerId(msg.localId, serverMsgId);
        msg.msgId = serverMsgId;
        msg.status = 'sent';

        // Update Session with serverMsgId
        const s = await chatRepository.getSession(sessionKey);
        if (s) {
          s.lastMsgId = serverMsgId;
          await chatRepository.saveSession(s);
        }
      }
    } catch (e) {
      console.error('sendTextMsg failed:', e);
      msg.status = 'failed';
      await chatRepository.updateMessageStatus(msg.localId, 'failed', 0);
    }

    // SessionUpdateEvent first, then NewMsgEvent (mirrors Flutter order and avoids
    // the React state batching race that could wipe the updated messages list).
    await this.notifySessionsUpdate();
    eventBus.emit('NewMsgEvent', { ...msg });

    return msg;
  }

  public async sendFileMsg(file: {
    assetId?: string;
    fileId?: string;
    transferId?: string;
    transport?: 'online' | 'offline';
    fileName: string;
    fileSize: number;
    sha256?: string;
    mimeType?: string | null;
  }, sessionId: number, localId?: string): Promise<ChatMessage> {
    if (!this.imClient || !this.userId) throw new Error('Client not initialized');

    const proto = getProto();
    const sessionKey = sessionKeyOf('single', sessionId);
    const payload = file.transport === 'online'
      ? buildOnlineFileMessagePayload({
        fileId: file.fileId || crypto.randomUUID(),
        transferId: file.transferId || '',
        fileName: file.fileName,
        fileSize: file.fileSize,
        sha256: file.sha256,
        mimeType: file.mimeType,
      })
      : buildFileMessagePayload({
        assetId: file.assetId || '',
        fileName: file.fileName,
        fileSize: file.fileSize,
        sha256: file.sha256,
        mimeType: file.mimeType,
      });
    const msgData = encodeFileMessagePayload(payload);
    const text = fileMessagePreview(payload);
    const fileMeta = parseFileMessagePayload(msgData, proto.IM.BaseDefine.MsgType.MSG_TYPE_SINGLE_FILE);
    const now = Date.now();

    const msg: ChatMessage = {
      localId: localId ?? nextLocalMessageId(),
      msgId: 0,
      sessionKey,
      sessionId,
      sessionType: 'single',
      from: this.userId,
      text,
      msgType: proto.IM.BaseDefine.MsgType.MSG_TYPE_SINGLE_FILE,
      msgData,
      fileMeta,
      ts: now,
      dir: 'out',
      status: 'pending',
    };

    await chatRepository.saveMessage(msg);

    let session = await chatRepository.getSession(sessionKey);
    if (!session) {
      session = {
        sessionKey,
        sessionId,
        sessionType: 'single',
        updatedAt: now,
        lastMsgId: 0,
        lastMsgPreview: text,
        lastMsgFromUserId: this.userId,
        unreadCount: 0,
        title: '',
        subtitle: '',
        avatarText: '',
        messages: [],
        historyState: 'idle',
        historyError: null,
        hasMoreHistory: true,
      };
    } else {
      session.updatedAt = now;
      session.lastMsgPreview = text;
      session.lastMsgFromUserId = this.userId;
    }

    await chatRepository.saveSession(session);
    eventBus.emit('NewMsgEvent', msg);

    try {
      const ack = await this.imClient.sendFileMessage(this.userId, sessionId, msgData);
      const serverMsgId = ensureSuccessfulAck(ack.getMsgId?.() ?? 0);
      if (serverMsgId > 0) {
        await chatRepository.updateMessageWithServerId(msg.localId, serverMsgId);
        msg.msgId = serverMsgId;
        msg.status = 'sent';

        const s = await chatRepository.getSession(sessionKey);
        if (s) {
          s.lastMsgId = serverMsgId;
          await chatRepository.saveSession(s);
        }
      }
    } catch (error) {
      msg.status = 'failed';
      await chatRepository.updateMessageStatus(msg.localId, 'failed', 0);
      eventBus.emit('NewMsgEvent', { ...msg });
      throw error;
    }

    await this.notifySessionsUpdate();
    eventBus.emit('NewMsgEvent', { ...msg });
    return msg;
  }

  public async fetchMissingMsgs(sessionKey: string, sessionId: number, sessionType: SessionKind, msgIdEnd: number, count: number) {
    if (msgIdEnd < 1 || !this.imClient || !this.userId) return 0;
    const msgIdBeg = Math.max(1, msgIdEnd - count);
    const localIds = await chatRepository.getMsgIdsInRange(sessionKey, msgIdBeg, msgIdEnd);
    
    const missing: number[] = [];
    for (let i = msgIdBeg; i <= msgIdEnd; i++) {
        if (!localIds.includes(i)) missing.push(i);
    }
    
    if (missing.length === 0) return 0;

    // Simulate flutter's fetch missing array (web client doesn't explicitly expose loadMsgsByIdList in ImClient, we would requestMessageHistory around ranges)
    // Here we request the block covering the range
    try {
      const response = await this.imClient.requestMessageHistory(
        this.userId,
        sessionId,
        sessionTypeToProtoValue(sessionType),
        msgIdEnd, 
        count + 5
      );
      
      const mapped: ChatMessage[] = response.getMsgListList().map((msgInfo: any) => {
          const msgType = msgInfo.getMsgType();
          const fromId = msgInfo.getFromSessionId?.() || msgInfo.getFromUserId?.();
          const bytes = msgInfo.getMsgData_asU8?.() ?? new Uint8Array();
          const text = this.decodeMsgData(bytes, msgType);

          return {
            localId: nextLocalMessageId(),
            msgId: msgInfo.getMsgId(),
            sessionKey,
            sessionId,
            sessionType,
            from: fromId,
            text,
            msgType,
            msgData: bytes,
            ts: msgInfo.getCreateTime() * 1000,
            dir: fromId === this.userId ? 'out' : 'in',
            status: 'sent',
        };
      });

      const insertedCount = await chatRepository.saveMessages(mapped);
      if (insertedCount > 0) {
        eventBus.emit('HistoryGapFilledEvent', sessionKey);
      }
      
      await this.notifySessionsUpdate();
      return insertedCount;
    } catch (error) {
      console.warn('fetchMissingMsgs fail', error);
      return 0;
    }
  }

  public resolveSessions(sessions: ChatSession[]): ChatSession[] {
    return sessions.filter((session) => (
      !(session.sessionType === 'single' && session.sessionId === this.userId)
    )).map(s => {
      if (s.sessionType === 'group') {
        const g = this.groupMap.get(s.sessionId);
        s.title = g?.name || `群组 #${s.sessionId}`;
        s.subtitle = g?.shieldStatus ? '已开启免打扰' : '群组讨论';
        s.avatarText = initials(g?.name || 'G', 'G');
      } else {
        const u = this.userMap.get(s.sessionId);
        s.title = u?.name || `用户 #${s.sessionId}`;
        s.subtitle = u?.signInfo || u?.email || '';
        s.avatarText = initials(u?.name || 'U', 'U');
      }
      return s;
    });
  }

  public decodeMsgData(bytes: Uint8Array, msgType: number, preview = false): string {
    const proto = getProto();
    const isText = msgType === proto.IM.BaseDefine.MsgType.MSG_TYPE_SINGLE_TEXT || 
                   msgType === proto.IM.BaseDefine.MsgType.MSG_TYPE_GROUP_TEXT;
    
    if (isText) {
      const encryptedText = new TextDecoder().decode(bytes);
      try {
        const decoded = this.imClient?.decrypt(encryptedText) || encryptedText;
        return preview ? msgTextToPreview(decoded) : decoded;
      } catch {
        return encryptedText;
      }
    }
    
    const isAudio = msgType === proto.IM.BaseDefine.MsgType.MSG_TYPE_SINGLE_AUDIO || 
                     msgType === proto.IM.BaseDefine.MsgType.MSG_TYPE_GROUP_AUDIO;
    if (isAudio) return AUDIO_PREVIEW_TEXT;
    if (msgType === proto.IM.BaseDefine.MsgType.MSG_TYPE_SINGLE_CALL ||
        msgType === proto.IM.BaseDefine.MsgType.MSG_TYPE_GROUP_CALL) return callMessagePreview(bytes);

    const fileMeta = parseFileMessagePayload(bytes, msgType);
    if (fileMeta) {
      return fileMessagePreview(fileMeta);
    }

    return "[未知类型消息]";
  }

  public async clearUnReadCntBySessionKey(sessionKey: string) {
    const session = await chatRepository.getSession(sessionKey);
    if (session) {
      session.unreadCount = 0;
      await chatRepository.saveSession(session);
      await this.notifySessionsUpdate();
    }
  }

  public ackMsgRead(msg: ChatMessage) {
    if (!this.imClient || !this.userId || msg.msgId <= 0) return;
    const proto = getProto();
    const sessionType = msg.sessionType === 'group' 
      ? proto.IM.BaseDefine.SessionType.SESSION_TYPE_GROUP 
      : proto.IM.BaseDefine.SessionType.SESSION_TYPE_SINGLE;
    
    this.imClient.ackMsgRead(this.userId, msg.sessionId, sessionType, msg.msgId);
  }

  private async notifySessionsUpdate() {
    const sessionsList = await chatRepository.getAllSessions();
    const resolvedList = this.resolveSessions(sessionsList);
    eventBus.emit('SessionUpdateEvent', resolvedList);
  }

  /**
   * Returns every user whose identity can currently affect the workspace.
   * This is the authoritative rebuild input when retained IM changes no longer
   * cover the client's cursor; cached presentation rows are never consulted.
   */
  public relevantIdentityUserIds(): number[] {
    const ids = new Set<number>(this.userMap.keys());
    for (const session of this.sessionMap.values()) {
      if (session.sessionType === 'single') ids.add(session.sessionId);
    }
    for (const group of this.groupMap.values()) {
      for (const memberId of group.memberIds ?? []) ids.add(memberId);
    }
    if (this.userId) ids.delete(this.userId);
    return [...ids].filter((id) => Number.isSafeInteger(id) && id > 0)
      .sort((left, right) => left - right);
  }

  public close() {
    this.imClient?.close();
    this.imClient = null;
    this.userId = null;
    this.userMap.clear();
    this.groupMap.clear();
    this.sessionMap.clear();
  }
}
